import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Moon, Sun, TrendingUp, TrendingDown, Wallet, Activity, Bell, LineChart, Rocket } from 'lucide-react';
import { useTheme } from '@/components/ThemeProvider';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import PriceChart from '@/components/PriceChart';
import PredictionsPanel from '@/components/PredictionsPanel';
import PortfolioPanel from '@/components/PortfolioPanel';
import TradingSimulator from '@/components/TradingSimulator';
import AlertsPanel from '@/components/AlertsPanel';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { MAINTENANCE_MODE } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [btcPrice, setBtcPrice] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portfolio, setPortfolio] = useState([]);
  const [predictions, setPredictions] = useState([]);

  const fetchPrice = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/price/current`);
      setBtcPrice(response.data);
    } catch (error) {
      console.error('Error fetching price:', error);
      toast.error('Failed to fetch Bitcoin price');
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/price/history?days=7`);
      setPriceHistory(response.data.history);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  }, []);

  const fetchPortfolio = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portfolio`);
      setPortfolio(response.data);
    } catch (error) {
      console.error('Error fetching portfolio:', error);
    }
  }, []);

  const fetchPredictions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/predictions`);
      setPredictions(response.data);
    } catch (error) {
      console.error('Error fetching predictions:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPrice(), fetchHistory(), fetchPortfolio(), fetchPredictions()]);
      setLoading(false);
    };
    loadData();

    const interval = setInterval(fetchPrice, 30000);
    return () => clearInterval(interval);
  }, [fetchPrice, fetchHistory, fetchPortfolio, fetchPredictions]);

  const calculatePortfolioValue = () => {
    if (!portfolio.length || !btcPrice) return { total: 0, pnl: 0, pnlPercent: 0 };
    const total = portfolio.reduce((sum, h) => sum + h.amount, 0);
    const currentValue = total * btcPrice.price;
    const invested = portfolio.reduce((sum, h) => sum + (h.amount * h.purchase_price), 0);
    const pnl = currentValue - invested;
    const pnlPercent = invested > 0 ? (pnl / invested) * 100 : 0;
    return { total: currentValue, pnl, pnlPercent };
  };

  const portfolioStats = calculatePortfolioValue();

  return (
    <div className="min-h-screen bg-background">
      {/* Spacer for fixed maintenance banner */}
      {MAINTENANCE_MODE && <div className="h-10 sm:h-12" />}
      
      {/* Header - adjusted for maintenance banner */}
      <header className="glass-effect border-b border-border/40 sticky z-50 top-0" style={MAINTENANCE_MODE ? { top: '2.5rem' } : {}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-purple-600 flex items-center justify-center">
                <LineChart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold tracking-tight">CryptoPulse Pro</h1>
                <p className="text-sm text-muted-foreground">AI-Powered Bitcoin Intelligence</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="marketing-hub-btn"
                variant="outline"
                onClick={() => navigate('/marketing')}
                className="hidden sm:flex"
              >
                <Rocket className="w-4 h-4 mr-2" />
                Marketing Hub
              </Button>
              <Button
                data-testid="theme-toggle"
                variant="ghost"
                size="icon"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="rounded-lg"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin w-12 h-12 border-4 border-bitcoin border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="space-y-8">
            {/* Top Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Card className="p-6 hover:shadow-md transition-all">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Current Price</p>
                    <Activity className="w-4 h-4 text-bitcoin" />
                  </div>
                  <p className="text-3xl font-bold font-heading" data-testid="current-price">
                    ${btcPrice?.price.toLocaleString()}
                  </p>
                  {btcPrice && (
                    <div className={`flex items-center gap-1 mt-2 text-sm font-medium ${btcPrice.change_24h >= 0 ? 'text-gain' : 'text-loss'}`}>
                      {btcPrice.change_24h >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                      {Math.abs(btcPrice.change_24h).toFixed(2)}%
                    </div>
                  )}
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card className="p-6 hover:shadow-md transition-all">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Portfolio Value</p>
                    <Wallet className="w-4 h-4 text-emerald-500" />
                  </div>
                  <p className="text-3xl font-bold font-heading" data-testid="portfolio-value">
                    ${portfolioStats.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <div className={`flex items-center gap-1 mt-2 text-sm font-medium ${portfolioStats.pnl >= 0 ? 'text-gain' : 'text-loss'}`}>
                    {portfolioStats.pnl >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                    ${Math.abs(portfolioStats.pnl).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({portfolioStats.pnlPercent.toFixed(2)}%)
                  </div>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Card className="p-6 hover:shadow-md transition-all">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">24h Volume</p>
                    <Activity className="w-4 h-4 text-blue-500" />
                  </div>
                  <p className="text-2xl font-bold font-heading">
                    ${btcPrice ? (btcPrice.volume_24h / 1e9).toFixed(2) : 0}B
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">Trading volume</p>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Card className="p-6 hover:shadow-md transition-all">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Active Predictions</p>
                    <Bell className="w-4 h-4 text-violet-500" />
                  </div>
                  <p className="text-3xl font-bold font-heading">
                    {predictions.length}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">AI forecasts</p>
                </Card>
              </motion.div>
            </div>

            {/* Bento Grid Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Price Chart - Large */}
              <motion.div
                className="lg:col-span-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <PriceChart history={priceHistory} currentPrice={btcPrice?.price} />
              </motion.div>

              {/* Predictions Panel */}
              <motion.div
                className="lg:col-span-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <PredictionsPanel predictions={predictions} onRefresh={fetchPredictions} currentPrice={btcPrice?.price} />
              </motion.div>

              {/* Portfolio */}
              <motion.div
                className="lg:col-span-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                <PortfolioPanel 
                  portfolio={portfolio} 
                  currentPrice={btcPrice?.price}
                  onRefresh={fetchPortfolio}
                />
              </motion.div>

              {/* Trading Simulator */}
              <motion.div
                className="lg:col-span-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                <TradingSimulator currentPrice={btcPrice?.price} onTrade={fetchPortfolio} />
              </motion.div>

              {/* Alerts */}
              <motion.div
                className="lg:col-span-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
              >
                <AlertsPanel currentPrice={btcPrice?.price} />
              </motion.div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;